// client/src/components/marketer/MarketerProjectResultCheck.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  readResultProjectList, 
  cacheResultProjectList,
  readResults,
  cacheResults 
} from '../../utils/marketerResultCache';
import ProjectDetailModal from './project/modals/ProjectDetailModal';
import ResultViewModal from './result/modals/ResultViewModal';
import ApproveModal from './result/modals/ApproveModal';
import RejectModal from './result/modals/RejectModal';
import ApproveCompleteModal from './result/modals/ApproveCompleteModal';
import RejectCompleteModal from './result/modals/RejectCompleteModal';

// ✅ 이미지 프리로드 함수 (브라우저 캐시 활용)
const preloadImage = (url) => {
  if (!url) return;
  const img = new Image();
  img.src = url;
};

// ✅ SNS 플랫폼 아이콘
const getSNSIcon = (platform) => {
  const iconMap = {
    instagram: '/images/sns/instagram.svg',
    youtube: '/images/sns/youtube.svg',
    blog: '/images/sns/blog.svg',
    tiktok: '/images/sns/tiktok.svg'
  };
  
  const iconPath = iconMap[platform] || '/images/sns/default.svg';
  
  return (
    <img 
      src={iconPath}
      alt={platform}
      style={{
        width: '16px',
        height: '16px',
        objectFit: 'contain',
        verticalAlign: 'middle'
      }}
    />
  );
};

// ✅ 팔로워 수 포맷팅 함수
const formatFollowerCount = (count) => {
  if (!count || count === 0) return '';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return String(count);
};

function MarketerProjectResultCheck() {
  const location = useLocation();
  const [uid, setUid] = useState(window.__ME__?.id);
  
  useEffect(() => {
    const currentUid = window.__ME__?.id;
    if (currentUid !== uid) {
      setUid(currentUid);
    }
    
    const handleUserReady = (event) => {
      const userId = event.detail?.id;
      if (userId) {
        setUid(userId);
      }
    };
    
    if (!window.__USER_READY_LISTENERS__) {
      window.__USER_READY_LISTENERS__ = [];
    }
    window.__USER_READY_LISTENERS__.push(handleUserReady);
    
    return () => {
      if (window.__USER_READY_LISTENERS__) {
        const idx = window.__USER_READY_LISTENERS__.indexOf(handleUserReady);
        if (idx > -1) {
          window.__USER_READY_LISTENERS__.splice(idx, 1);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const initialProjects = useMemo(() => {
    return uid ? (readResultProjectList(uid) || []) : [];
  }, [uid]);
  
  const hasProjectCache = uid && initialProjects.length > 0;
  
  const initialResults = useMemo(() => {
    if (!hasProjectCache || !initialProjects[0]?.id) return [];
    return readResults(initialProjects[0].id, 'all') || [];
  }, [hasProjectCache, initialProjects]);
  
  const hasResultCache = initialResults.length > 0;
  
  const [projects, setProjects] = useState(initialProjects);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [results, setResults] = useState(initialResults);
  const [loading, setLoading] = useState(!hasProjectCache);
  const [resultsLoading, setResultsLoading] = useState(!hasResultCache && hasProjectCache);
  const [error, setError] = useState(null);
  const [filterPendingOnly, setFilterPendingOnly] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!hasProjectCache);
  
  const [viewModal, setViewModal] = useState({
    open: false,
    result: null
  });
  
  const [approveModal, setApproveModal] = useState({
    open: false,
    applicationId: null,
    influencerName: ''
  });
  
  const [rejectModal, setRejectModal] = useState({
    open: false,
    applicationId: null,
    influencerName: ''
  });
  
  const [completeModal, setCompleteModal] = useState({
    open: false,
    type: null // 'approve' or 'reject'
  });

  const [refreshing, setRefreshing] = useState(false);

  const prefetchResults = useCallback(async (projectId) => {
    if (!projectId) return;
    
    const filter = filterPendingOnly ? 'pending' : 'all';
    
    const cached = readResults(projectId, filter);
    if (cached) {
      return;
    }
    
    try {
      const res = await fetch(
        `/api/marketer/projects/${projectId}/results?filter=${filter}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      
      if (res.ok && data.ok && data.results) {
        cacheResults(projectId, data.results, filter);
        console.log(`✅ 프로젝트 ${projectId} 결과물 프리페치 완료 (${data.results.length}건)`);
      }
    } catch (e) {
      console.warn(`결과물 프리페치 실패 (${projectId}):`, e);
    }
  }, [filterPendingOnly]);

  const fetchResults = useCallback(async (projectId, force = false) => {
    const filter = filterPendingOnly ? 'pending' : 'all';
    
    // ✅ 캐시 우선 확인 (force가 false일 때만)
    if (!force) {
      const cached = readResults(projectId, filter);
      if (cached) {
        setResults(cached);
        setResultsLoading(false);
        return;
      }
    }

    setResultsLoading(true);
    try {
      const res = await fetch(
        `/api/marketer/projects/${projectId}/results?filter=${filter}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      
      if (!res.ok || !data.ok) {
        throw new Error(data.message || '결과물 목록 조회 실패');
      }

      setResults(data.results || []);
      cacheResults(projectId, data.results || [], filter);
      
    } catch (e) {
      console.error('결과물 목록 조회 오류:', e);
      setError(e.message);
      setResults([]);
    } finally {
      setResultsLoading(false);
    }
  }, [filterPendingOnly]);

  const fetchProjects = useCallback(async (force = false) => {
    if (!uid) return;

    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch('/api/marketer/projects/with-results', {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (!res.ok || !data.ok) {
        throw new Error(data.message || '프로젝트 목록 조회 실패');
      }

      const fetchedProjects = data.projects || [];
      setProjects(fetchedProjects);
      
      cacheResultProjectList(uid, fetchedProjects);
      
      if (fetchedProjects.length > 0) {
        await fetchResults(fetchedProjects[0].id);
        
        setTimeout(() => {
          if (fetchedProjects[0]?.coverImage) {
            preloadImage(fetchedProjects[0].coverImage);
          }
          if (fetchedProjects[1]?.coverImage) {
            preloadImage(fetchedProjects[1].coverImage);
          }
        }, 0);
      }
      
    } catch (e) {
      console.error('프로젝트 목록 조회 오류:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [uid, fetchResults]);

  useEffect(() => {
    if (uid && initialProjects.length > 0) {
      setProjects(initialProjects);
      setLoading(false);
      if (initialResults.length > 0) {
        setResults(initialResults);
        setResultsLoading(false);
      }
      setIsInitialLoad(false);
    } else if (uid && initialProjects.length === 0) {
      setLoading(true);
      setIsInitialLoad(true);
    }
  }, [uid, initialProjects, initialResults]);

  useEffect(() => {
    if (!isInitialLoad) {
      if (projects.length > 0) {
        if (projects[0]?.coverImage) {
          preloadImage(projects[0].coverImage);
        }
        if (projects[1]?.coverImage) {
          preloadImage(projects[1].coverImage);
        }
        
        if (projects[1]?.id) {
          setTimeout(() => {
            prefetchResults(projects[1].id);
          }, 100);
        }
      }
      return;
    }
    
    const loadProjects = async () => {
      await fetchProjects();
      setIsInitialLoad(false);
    };
    loadProjects();
  }, [fetchProjects, isInitialLoad, projects, prefetchResults]);

  useEffect(() => {
    if (!isInitialLoad && projects.length > 0 && currentProjectIndex < projects.length) {
      fetchResults(projects[currentProjectIndex].id);
    }
  }, [filterPendingOnly, currentProjectIndex, projects, fetchResults, isInitialLoad]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetProjectId = params.get('projectId');
    
    if (targetProjectId && projects.length > 0 && !isInitialLoad) {
      const targetIndex = projects.findIndex(p => p.id === targetProjectId);
      if (targetIndex !== -1 && targetIndex !== currentProjectIndex) {
        setCurrentProjectIndex(targetIndex);
        const newParams = new URLSearchParams(location.search);
        newParams.delete('projectId');
        window.history.replaceState({}, '', `${location.pathname}?${newParams.toString()}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, location.search, isInitialLoad]);

  const currentProject = projects[currentProjectIndex];

  const handlePrevProject = () => {
    if (currentProjectIndex > 0) {
      const newIndex = currentProjectIndex - 1;
      const nextProjectId = projects[newIndex]?.id;
      
      if (nextProjectId) {
        const filter = filterPendingOnly ? 'pending' : 'all';
        const cached = readResults(nextProjectId, filter);
        if (cached) {
          setResults(cached);
        }
      }
      
      setCurrentProjectIndex(newIndex);
      
      if (newIndex > 0 && projects[newIndex - 1]?.coverImage) {
        preloadImage(projects[newIndex - 1].coverImage);
      }
      
      if (newIndex > 0 && projects[newIndex - 1]?.id) {
        setTimeout(() => {
          prefetchResults(projects[newIndex - 1].id);
        }, 100);
      }
    }
  };

  const handleNextProject = () => {
    if (currentProjectIndex < projects.length - 1) {
      const newIndex = currentProjectIndex + 1;
      const nextProjectId = projects[newIndex]?.id;
      
      if (nextProjectId) {
        const filter = filterPendingOnly ? 'pending' : 'all';
        const cached = readResults(nextProjectId, filter);
        if (cached) {
          setResults(cached);
        }
      }
      
      setCurrentProjectIndex(newIndex);
      
      if (newIndex < projects.length - 1 && projects[newIndex + 1]?.coverImage) {
        preloadImage(projects[newIndex + 1].coverImage);
      }
      
      if (newIndex < projects.length - 1 && projects[newIndex + 1]?.id) {
        setTimeout(() => {
          prefetchResults(projects[newIndex + 1].id);
        }, 100);
      }
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'none': '제출 대기',
      'submitted': '결과물 승인 대기',
      'approved': '결과물 승인 완료',
      'rejected': '거절 (관리자 검토 중)',
      'admin_reviewing': '거절 (셀프애드 팀 관리 중)',
      'admin_approved': '셀프애드팀 결과물 승인 처리',
      'admin_rejected': '셀프애드팀 거절 확정 (계약 취소)',
      'expired': '기한 내 미제출',
      'completed': '완료'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'none': 'var(--mb-text-sub)',
      'submitted': 'var(--mb-color-warning)',
      'approved': 'var(--mb-color-secondary)',
      'rejected': '#8b5cf6',
      'admin_reviewing': '#8b5cf6',
      'admin_approved': 'var(--mb-color-secondary)',
      'admin_rejected': 'var(--mb-color-danger)',
      'expired': 'var(--mb-text-sub)',
      'completed': 'var(--mb-color-secondary)'
    };
    return colorMap[status] || 'var(--mb-text-sub)';
  };

  const handleViewResult = (result) => {
    setViewModal({
      open: true,
      result
    });
  };

  const handleApproveStart = (applicationId, influencerName) => {
    setApproveModal({
      open: true,
      applicationId,
      influencerName
    });
  };

  const handleApproveConfirm = async (satisfactionScore) => {
    const { applicationId } = approveModal;
    
    try {
      const res = await fetch(`/api/marketer/projects/results/${applicationId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ satisfactionScore })
      });
      
      const data = await res.json();
      
      if (!res.ok || !data.ok) {
        throw new Error(data.message || '승인 처리 실패');
      }
      
      setApproveModal({ open: false, applicationId: null, influencerName: '' });
      setCompleteModal({ open: true, type: 'approve' });
      
      if (currentProject?.id) {
        await fetchResults(currentProject.id, true);
      }
      
    } catch (e) {
      console.error('승인 처리 오류:', e);
      alert(e.message || '승인 처리 중 오류가 발생했습니다.');
      setApproveModal({ open: false, applicationId: null, influencerName: '' });
    }
  };

  const handleRejectStart = (applicationId, influencerName) => {
    setRejectModal({
      open: true,
      applicationId,
      influencerName
    });
  };

  const handleRejectConfirm = async (reason) => {
    const { applicationId } = rejectModal;
    
    try {
      const res = await fetch(`/api/marketer/projects/results/${applicationId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rejectionReason: reason })
      });
      
      const data = await res.json();
      
      if (!res.ok || !data.ok) {
        throw new Error(data.message || '거절 처리 실패');
      }
      
      setRejectModal({ open: false, applicationId: null, influencerName: '' });
      setCompleteModal({ open: true, type: 'reject' });
      
      if (currentProject?.id) {
        await fetchResults(currentProject.id, true);
      }
      
    } catch (e) {
      console.error('거절 처리 오류:', e);
      alert(e.message || '거절 처리 중 오류가 발생했습니다.');
      setRejectModal({ open: false, applicationId: null, influencerName: '' });
    }
  };

  // ✅ 새로고침 함수
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchProjects(true); // force=true
      if (currentProject) {
        await fetchResults(currentProject.id, true); // force=true
      }
    } catch (e) {
      console.error('새로고침 오류:', e);
    } finally {
      setRefreshing(false);
    }
  }, [fetchProjects, fetchResults, currentProject]);

  if (loading && !projects.length) {
    return (
      <div className="mb-card" style={{ textAlign: 'center', padding: 40 }}>
        <div>로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-card" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ color: 'var(--mb-color-danger)', marginBottom: 16 }}>
          오류가 발생했습니다: {error}
        </div>
        <button onClick={() => { setError(null); fetchProjects(); }} className="mb-btn mb-btn-secondary">
          다시 시도
        </button>
      </div>
    );
  }

  if (!projects.length) {
    return (
      <div className="mb-card" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ color: 'var(--mb-text-sub)' }}>
          결과물 관리 대상 프로젝트가 없습니다.
        </div>
      </div>
    );
  }

  return (
    <div className="mb-card mb-card--full-height">
      {/* 상단 안내 */}
      <div className="mb-page-header">
        <h2 className="mb-page-title">결과물 확인하기</h2>
        <p className="mb-page-description">인플루언서가 제출한 결과물을 확인하고 승인 또는 거절해주세요.</p>
      </div>

      {/* 프로젝트 네비게이션 카드 */}
      <div className="pnav">
        <div className="pnav__top">
          <div className="pnav__pager">
            <button
              onClick={handlePrevProject}
              disabled={currentProjectIndex === 0}
              aria-label="이전 프로젝트"
            >
              ‹
            </button>
            <span>{currentProjectIndex + 1} / {projects.length}</span>
            <button
              onClick={handleNextProject}
              disabled={currentProjectIndex === projects.length - 1}
              aria-label="다음 프로젝트"
            >
              ›
            </button>
          </div>
          <div className="pnav__spacer" />
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="mb-btn mb-btn-secondary mb-btn-sm"
          >
            {refreshing ? '불러오는 중...' : '새로고침'}
          </button>
          <button
            onClick={() => setShowDetailModal(true)}
            className="mb-btn mb-btn-secondary mb-btn-sm"
          >
            프로젝트 상세보기
          </button>
        </div>

        {currentProject && (
          <>
            <div className="pnav__no">PROJECT NO.{currentProject.projectNumber || 'N/A'}</div>
            {currentProject.product && (
              <div className="pnav__title">
                {currentProject.product.name || currentProject.product.placeName || currentProject.product.customTitle}
                을(를) 홍보하는{' '}
                {currentProject.serviceType === 'delivery' && '제품 제공'}
                {currentProject.serviceType === 'purchase' && '제품 미제공'}
                {currentProject.serviceType === 'visit' && '장소 방문'}
                {currentProject.serviceType === 'free' && '자율'}
                {!['delivery', 'purchase', 'visit', 'free'].includes(currentProject.serviceType) && '일반'}
                {' '}형식의 프로젝트입니다.
              </div>
            )}

            <div className="pnav__stats">
              <div className="pnav__stat">
                <b>{(currentProject.stats?.submitted || 0) + (currentProject.stats?.approved || 0)}</b>
                <span>결과물 제출</span>
              </div>
              <div className="pnav__stat">
                <b>{currentProject.stats?.submitted || 0}</b>
                <span>승인 대기</span>
              </div>
              <div className="pnav__stat">
                <b>{currentProject.stats?.total || 0}</b>
                <span>총 계약</span>
              </div>
            </div>

            <div className="pnav__dates">
              <span>
                결과물 제출 마감{' '}
                <b>{currentProject.submitEnd ? new Date(currentProject.submitEnd).toLocaleDateString('ko-KR') : 'N/A'}</b>
              </span>
              <span>
                결과물 승인 마감{' '}
                <b>{currentProject.displayUntil ? new Date(currentProject.displayUntil).toLocaleDateString('ko-KR') : 'N/A'}</b>
              </span>
            </div>

            {currentProject.displayUntil && (
              <div className="note note--blue">
                결과물 승인/거절은 <b>{new Date(currentProject.displayUntil).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' }).replace(/\. /g, '.').replace(/\.$/, '')}</b>까지 가능합니다.
              </div>
            )}
          </>
        )}
      </div>

      {/* 필터 */}
      <div className="chips">
        <button
          type="button"
          className={`chip${!filterPendingOnly ? ' is-active' : ''}`}
          onClick={() => setFilterPendingOnly(false)}
        >
          전체
        </button>
        <button
          type="button"
          className={`chip${filterPendingOnly ? ' is-active' : ''}`}
          onClick={() => setFilterPendingOnly(true)}
        >
          승인 대기만 보기
        </button>
      </div>

      {/* A안: 승인 대기 카운트 히어로 */}
      {currentProject && (currentProject.stats?.submitted || 0) > 0 && (
        <div className="mbh-count">
          <b>{currentProject.stats.submitted}</b>
          <span>건의 결과물이 승인을 기다리고 있어요</span>
        </div>
      )}

      {/* 결과물 목록 테이블 */}
      {resultsLoading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: 60, 
          color: 'var(--mb-text-sub)',
          background: 'white',
          borderRadius: 'var(--mb-card-radius)',
          border: '1px solid var(--mb-border-color)'
        }}>
          <div style={{ fontSize: 15 }}>결과물 목록을 불러오는 중...</div>
        </div>
      ) : results.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: 60, 
          color: 'var(--mb-text-sub)',
          background: 'white',
          borderRadius: 'var(--mb-card-radius)',
          border: '1px solid var(--mb-border-color)'
        }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: 'var(--mb-text-body)' }}>
            {currentProject?.stats?.total === 0 
              ? '아직 인플루언서와 계약이 이루어지지 않은 프로젝트입니다.'
              : '제출된 결과물이 없습니다.'}
          </div>
          {currentProject?.stats?.total === 0 && (
            <div style={{ fontSize: 14 }}>
              인플루언서와 계약이 완료되면 결과물을 확인할 수 있습니다.
            </div>
          )}
        </div>
      ) : (
        <div className="mb-table-container">
          <table className="mb-table">
            <thead>
              <tr>
                <th style={{ minWidth: 50 }}>NO</th>
                <th style={{ minWidth: 120 }}>인플루언서</th>
                <th style={{ minWidth: 100, textAlign: 'center' }}>결과물</th>
                <th style={{ minWidth: 180, textAlign: 'center' }}>승인 및 거절</th>
                <th style={{ minWidth: 150, textAlign: 'center' }}>상태</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result, index) => (
                <ResultRow
                  key={result.id}
                  result={result}
                  index={index}
                  rewardPerCreator={currentProject?.rewardPerCreator || 0}
                  canTakeAction={currentProject?.canTakeAction ?? true}
                  getStatusText={getStatusText}
                  getStatusColor={getStatusColor}
                  onView={handleViewResult}
                  onApprove={handleApproveStart}
                  onReject={handleRejectStart}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 모달들 */}
      {showDetailModal && currentProject && (
        <ProjectDetailModal
          open={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          project={currentProject}
        />
      )}

      {viewModal.open && (
        <ResultViewModal
          open={viewModal.open}
          onClose={() => setViewModal({ open: false, result: null })}
          result={viewModal.result}
        />
      )}

      {approveModal.open && (
        <ApproveModal
          open={approveModal.open}
          onClose={() => setApproveModal({ open: false, applicationId: null, influencerName: '' })}
          influencerName={approveModal.influencerName}
          onConfirm={handleApproveConfirm}
        />
      )}

      {rejectModal.open && (
        <RejectModal
          open={rejectModal.open}
          onClose={() => setRejectModal({ open: false, applicationId: null, influencerName: '' })}
          influencerName={rejectModal.influencerName}
          onConfirm={handleRejectConfirm}
        />
      )}

      {completeModal.open && (
        completeModal.type === 'approve' ? (
          <ApproveCompleteModal
            open={completeModal.open}
            onClose={() => setCompleteModal({ open: false, type: null })}
          />
        ) : (
          <RejectCompleteModal
            open={completeModal.open}
            onClose={() => setCompleteModal({ open: false, type: null })}
          />
        )
      )}
    </div>
  );
}

// ✅ 결과물 행 컴포넌트 - React.memo로 최적화
const ResultRow = React.memo(({
  result,
  index,
  rewardPerCreator,
  canTakeAction: projectCanTakeAction,
  getStatusText,
  getStatusColor,
  onView,
  onApprove,
  onReject
}) => {
  
  const canViewResult = result.resultStatus === 'submitted' || 
                        result.resultStatus === 'approved' || 
                        result.resultStatus === 'rejected' ||
                        result.resultStatus === 'admin_reviewing' ||
                        result.resultStatus === 'admin_approved';
  
  // ✅ 결과물 상태가 submitted이고 프로젝트 승인 기간 내일 때만 승인/거절 가능
  const canTakeAction = result.resultStatus === 'submitted' && projectCanTakeAction;
  
  const statusText = getStatusText(result.resultStatus);
  const statusColor = getStatusColor(result.resultStatus);

  // ✅ 기존 getStatusColor 결과 → mb-badge 클래스 매핑 (표기 전용)
  const badgeClassByColor = {
    'var(--mb-color-warning)': 'mb-badge-amber',
    'var(--mb-color-secondary)': 'mb-badge-green',
    '#8b5cf6': 'mb-badge-violet',
    'var(--mb-color-danger)': 'mb-badge-red',
    'var(--mb-text-sub)': 'mb-badge-gray'
  };
  const statusBadgeClass = badgeClassByColor[statusColor] || 'mb-badge-gray';

  return (
    <tr>
      <td data-label="NO" className="t-num">{index + 1}</td>
      <td data-label="인플루언서">
        <div className="t-name">{result.influencerName || 'N/A'}</div>
        {result.snsAccounts && result.snsAccounts.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4, alignItems: 'flex-start' }}>
            {result.snsAccounts.map((sns, idx) => (
              sns.url ? (
                <a
                  key={idx}
                  href={sns.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="platform" style={{ fontSize: '12.5px' }}>
                    {getSNSIcon(sns.platform)}
                    @{sns.accountName}
                    {sns.followerCount > 0 && ` · ${formatFollowerCount(sns.followerCount)}`}
                  </span>
                </a>
              ) : (
                <span key={idx} className="platform" style={{ fontSize: '12.5px' }}>
                  {getSNSIcon(sns.platform)}
                  @{sns.accountName}
                  {sns.followerCount > 0 && ` · ${formatFollowerCount(sns.followerCount)}`}
                </span>
              )
            ))}
          </div>
        )}
        {(result.userAddress || result.phoneNumber) && (
          <div className="t-sub">
            {result.phoneNumber && (
              <div>{result.phoneNumber}</div>
            )}
            {result.userAddress && (
              <div>{result.userAddress}</div>
            )}
          </div>
        )}
      </td>
      <td data-label="결과물">
        {canViewResult ? (
          <button
            onClick={() => onView(result)}
            className="mb-btn mb-btn-secondary mb-btn-sm"
          >
            결과물 보기
          </button>
        ) : (
          <span className="t-sub" style={{ display: 'inline' }}>-</span>
        )}
      </td>
      <td data-label="승인 및 거절">
        {result.resultStatus === 'submitted' ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              disabled={!canTakeAction}
              onClick={() => canTakeAction && onApprove(result.id, result.influencerName)}
              title={!canTakeAction ? '승인 기간이 만료되었습니다 (submitEnd + 7일)' : ''}
              className="mb-btn mb-btn-mint mb-btn-sm"
            >
              {rewardPerCreator > 0 ? `승인 · ${Number(rewardPerCreator).toLocaleString()}P 지급` : '승인'}
            </button>
            <button
              disabled={!canTakeAction}
              onClick={() => canTakeAction && onReject(result.id, result.influencerName)}
              title={!canTakeAction ? '승인 기간이 만료되었습니다 (submitEnd + 7일)' : ''}
              className="mb-btn mb-btn-danger mb-btn-sm"
            >
              반려
            </button>
          </div>
        ) : (
          <span className="t-sub" style={{ display: 'inline' }}>
            {result.resultStatus === 'approved' ? '승인 완료' :
             ['rejected', 'admin_reviewing', 'admin_approved', 'admin_rejected'].includes(result.resultStatus) ? '거절 처리됨' :
             '-'}
          </span>
        )}
      </td>
      <td data-label="상태">
        <span className={`mb-badge ${statusBadgeClass}`}>
          {statusText}
        </span>
      </td>
    </tr>
  );
});

export default MarketerProjectResultCheck;
